import React, { useEffect, useState } from 'react';
import { getTeams, getStreamers, Team, TeamMember, TeamLeader, Streamer, getTeamLeaders, addTeamLeader, updateTeamLeaderRole, removeTeamLeader, getTeamRemu, updateTeamRemu, TeamRemu, NiveauRates } from './api';
import LiveAvatar from './LiveAvatar';

interface Objectives { days: number; hours: number; diamonds: number; }

interface SummaryData {
  totalDiamonds: number;
  totalRemuNiveau: number;
  totalRemuActivite: number;
  totalRemuProgressif: number;
  teamCount: number;
  period?: string;
}

interface Props {
  onSelectStreamer: (id: number) => void;
  refreshKey: number;
  activePeriod?: string;
  isMobile?: boolean;
  isAdmin?: boolean;
  objectives?: Objectives;
  searchTerm?: string;
  filterLive?: boolean;
  filterObjAny?: boolean;
  filterObjAll?: boolean;
  onSummaryChange?: (data: SummaryData) => void;
}

const fmtNum = (n?: number | null) => (n != null ? n.toLocaleString('fr-FR') : '—');
const fmtDuration = (mins?: number | null) => {
  if (!mins) return '—';
  return `${Math.floor(mins / 60)}h ${mins % 60}min`;
};
// const fmtRemu = (v?: number | null) => (v != null && v > 0 ? `$${v.toFixed(2)}` : '—'); // unused since summary banner moved to StreamerList

export default function TeamView({ onSelectStreamer, refreshKey, activePeriod, isMobile, isAdmin, objectives, searchTerm, filterLive, filterObjAny, filterObjAll, onSummaryChange }: Props) {
  const [teams, setTeams] = useState<Team[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [leaders, setLeaders] = useState<Record<string, TeamLeader[]>>({});
  const [allStreamers, setAllStreamers] = useState<Streamer[]>([]);
  const [teamRemus, setTeamRemus] = useState<Record<string, TeamRemu>>({});

  useEffect(() => {
    const notifySummary = (ts: Team[]) => {
      if (onSummaryChange && ts.length > 0) {
        onSummaryChange({
          totalDiamonds:      ts.reduce((s, t) => s + (t.total_diamonds       || 0), 0),
          totalRemuNiveau:    ts.reduce((s, t) => s + (t.total_remu_niveau    || 0), 0),
          totalRemuActivite:  ts.reduce((s, t) => s + (t.total_remu_activite  || 0), 0),
          totalRemuProgressif:ts.reduce((s, t) => s + (t.total_remu_progressif|| 0), 0),
          teamCount: ts.length,
          period: ts[0]?.period ?? undefined,
        });
      }
    };
    setLoading(true);
    getTeams(activePeriod).then(r => { setTeams(r.data); setLoading(false); notifySummary(r.data); }).catch(() => setLoading(false));
    const iv = setInterval(() => getTeams(activePeriod).then(r => { setTeams(r.data); notifySummary(r.data); }).catch(() => {}), 30000);
    return () => clearInterval(iv);
  }, [refreshKey, activePeriod]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getStreamers().then(r => setAllStreamers(r.data)).catch(() => {});
  }, []);

  const toggle = (groupe: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(groupe)) {
        next.delete(groupe);
      } else {
        next.add(groupe);
        // Load leaders for this team
        getTeamLeaders(groupe).then(r => setLeaders(prev => ({ ...prev, [groupe]: r.data }))).catch(() => {});
        getTeamRemu(groupe).then(r => setTeamRemus(prev => ({ ...prev, [groupe]: r.data }))).catch(() => {});
      }
      return next;
    });
  };

  if (loading) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
      Chargement…
    </div>
  );

  if (teams.length === 0) return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', gap: 12 }}>
      <div style={{ fontSize: 40, opacity: 0.3 }}>🏆</div>
      <div style={{ fontSize: 14 }}>Aucune équipe — importez un fichier Excel Backstage</div>
    </div>
  );

  // Filtre membres : recherche + live + objectifs
  const memberMatchesFilters = (m: TeamMember): boolean => {
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      if (!m.display_name.toLowerCase().includes(q) && !m.username.toLowerCase().includes(q)) return false;
    }
    if (filterLive && !m.tiktok_live) return false;
    if ((filterObjAny || filterObjAll) && objectives) {
      const met = [
        (m.live_days ?? 0) >= objectives.days,
        (m.live_duration_minutes ?? 0) >= objectives.hours * 60,
        (m.diamonds ?? 0) >= objectives.diamonds,
      ].filter(Boolean).length;
      if (filterObjAll && met < 3) return false;
      if (filterObjAny && !filterObjAll && met < 1) return false;
    }
    return true;
  };

  const hasActiveFilter = !!(searchTerm || filterLive || filterObjAny || filterObjAll);

  // Équipes affichées :
  //  - Si recherche texte correspond au nom de l'équipe → afficher (tous membres filtrés en interne)
  //  - Sinon → n'afficher que si au moins un membre passe TOUS les filtres actifs
  const displayTeams = hasActiveFilter
    ? teams.filter(t => {
        const nameMatch = searchTerm
          ? t.groupe.toLowerCase().includes(searchTerm.toLowerCase())
          : false;
        return nameMatch || t.members.some(memberMatchesFilters);
      })
    : teams;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* ── Desktop table header ── */}
      {!isMobile && (
        <div style={{ display: 'grid', gridTemplateColumns: isAdmin ? '1fr 70px 130px 110px 60px 80px 80px 80px 32px' : '1fr 70px 130px 110px 60px 80px 80px 32px', gap: 8, padding: '10px 32px 8px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <span style={thStyle}>Équipe</span>
          <span style={thStyle}>Membres</span>
          <span style={thStyle}>💎 Diamants</span>
          <span style={thStyle}>Durée live</span>
          <span style={thStyle}>Jours</span>
          <span style={{ ...thStyle, textAlign: 'center' }}>Montée niv.</span>
          <span style={{ ...thStyle, textAlign: 'center' }}>Activité</span>
          {isAdmin && <span style={{ ...thStyle, textAlign: 'center' }}>Progressif</span>}
          <span style={thStyle}></span>
        </div>
      )}

      {/* ── Teams list ── */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: isMobile ? '8px 8px 120px' : '8px 32px 120px' }}>
        {displayTeams.length === 0 && searchTerm ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
            <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.4 }}>🏆</div>
            <div style={{ fontSize: 14 }}>Aucune équipe ne correspond à la recherche</div>
          </div>
        ) : null}
        {displayTeams.map(team => {
          const isOpen = expanded.has(team.groupe);
          const totalD = teams.reduce((s, t) => s + (t.total_diamonds || 0), 0);
          const pct = totalD > 0 ? Math.round((team.total_diamonds / totalD) * 100) : 0;
          const hasRemu = (team.total_remu_niveau || 0) + (team.total_remu_activite || 0) + (team.total_remu_progressif || 0) > 0;

          return (
            <div key={team.groupe} style={{ marginBottom: 6, borderRadius: 12, border: '1px solid #e2e8f0', background: '#fff', overflow: 'hidden' }}>

              {/* Team row */}
              {isMobile ? (
                /* ── Mobile team card ── */
                <div onClick={() => toggle(team.groupe)}
                  style={{ padding: '12px 14px', cursor: 'pointer', background: isOpen ? '#f8fafc' : '#fff', transition: 'background 0.12s' }}>
                  {/* Header: name + chevron */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{team.groupe}</span>
                    <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#94a3b8" strokeWidth="2"
                      style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', flexShrink: 0 }}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  {/* Stats chips row */}
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <MiniStat label="Membres" value={String(team.member_count)} />
                    <MiniStat label="💎" value={fmtNum(team.total_diamonds)} />
                    <MiniStat label="Jours" value={String(team.total_days || '—')} />
                    <MiniStat label="Durée" value={fmtDuration(team.total_duration)} />
                  </div>
                  {/* Remu pills (only if any) */}
                  {hasRemu && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                      {(team.total_remu_niveau || 0) > 0 && (
                        <RemuPill label="Montée" value={`$${team.total_remu_niveau!.toFixed(2)}`} color="#15803d" bg="#f0fdf4" small />
                      )}
                      {(team.total_remu_activite || 0) > 0 && (
                        <RemuPill label="Activité" value={`$${team.total_remu_activite!.toFixed(2)}`} color="#2563eb" bg="#eff6ff" small />
                      )}
                      {isAdmin && (team.total_remu_progressif || 0) > 0 && (
                        <RemuPill label="Progressif" value={`$${team.total_remu_progressif!.toFixed(2)}`} color="#7c3aed" bg="#f5f3ff" small />
                      )}
                    </div>
                  )}
                </div>
              ) : (
                /* ── Desktop team row ── */
                <div onClick={() => toggle(team.groupe)}
                  style={{ display: 'grid', gridTemplateColumns: isAdmin ? '1fr 70px 130px 110px 60px 80px 80px 80px 32px' : '1fr 70px 130px 110px 60px 80px 80px 32px', gap: 8, padding: '14px 16px', alignItems: 'center', cursor: 'pointer', background: isOpen ? '#f8fafc' : '#fff', transition: 'background 0.12s' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{team.groupe}</span>
                    </div>
                    <div style={{ marginTop: 5, height: 4, borderRadius: 4, background: '#f1f5f9', maxWidth: 260, overflow: 'hidden' }}>
                      <div style={{ height: '100%', borderRadius: 4, background: '#2563eb', width: `${pct}%`, transition: 'width 0.4s' }} />
                    </div>
                  </div>
                  <div style={{ color: '#374151', fontSize: 13, fontWeight: 500 }}>{team.member_count}</div>
                  <div style={{ color: '#0f172a', fontSize: 14, fontWeight: 700 }}>{fmtNum(team.total_diamonds)}</div>
                  <div style={{ color: '#64748b', fontSize: 13 }}>{fmtDuration(team.total_duration)}</div>
                  <div style={{ color: '#64748b', fontSize: 13 }}>{team.total_days || '—'}</div>
                  <div style={{ color: (team.total_remu_niveau || 0) > 0 ? '#15803d' : '#94a3b8', fontSize: 12, fontWeight: 600, textAlign: 'center' }}>
                    {(team.total_remu_niveau || 0) > 0 ? `$${team.total_remu_niveau!.toFixed(2)}` : '—'}
                  </div>
                  <div style={{ color: (team.total_remu_activite || 0) > 0 ? '#2563eb' : '#94a3b8', fontSize: 12, fontWeight: 600, textAlign: 'center' }}>
                    {(team.total_remu_activite || 0) > 0 ? `$${team.total_remu_activite!.toFixed(2)}` : '—'}
                  </div>
                  {isAdmin && (
                    <div style={{ color: (team.total_remu_progressif || 0) > 0 ? '#7c3aed' : '#94a3b8', fontSize: 12, fontWeight: 600, textAlign: 'center' }}>
                      {(team.total_remu_progressif || 0) > 0 ? `$${team.total_remu_progressif!.toFixed(2)}` : '—'}
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#94a3b8" strokeWidth="2"
                      style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              )}

              {/* Members list */}
              {isOpen && (
                <div style={{ borderTop: '1px solid #f1f5f9' }}>
                  <LeadersSection
                    groupe={team.groupe}
                    leaders={leaders[team.groupe] || []}
                    allStreamers={allStreamers}
                    isAdmin={isAdmin}
                    isMobile={isMobile}
                    onLeaderAdded={(l) => setLeaders(prev => ({ ...prev, [team.groupe]: [...(prev[team.groupe] || []), l] }))}
                    onLeaderRemoved={(sid) => setLeaders(prev => ({ ...prev, [team.groupe]: (prev[team.groupe] || []).filter(l => l.streamer_id !== sid) }))}
                    onLeaderRoleChanged={(sid, role) => setLeaders(prev => ({ ...prev, [team.groupe]: (prev[team.groupe] || []).map(l => l.streamer_id === sid ? { ...l, role: role as any } : l) }))}
                  />
                  <div onClick={e => e.stopPropagation()} style={{ padding: isMobile ? '0 14px 12px' : '0 20px 12px' }}>
                    <RemuSection
                      groupe={team.groupe}
                      remu={teamRemus[team.groupe]}
                      isAdmin={isAdmin}
                      activePeriod={activePeriod}
                      onSaved={r => setTeamRemus(prev => ({ ...prev, [team.groupe]: r }))}
                    />
                  </div>
                  {!isMobile && (
                    <div style={{ display: 'grid', gridTemplateColumns: isAdmin ? '1fr 120px 100px 60px 80px 80px 80px' : '1fr 120px 100px 60px 80px 80px', gap: 8, padding: '8px 20px 6px', background: '#fafbfc' }}>
                      <span style={{ ...thStyle, fontSize: 10 }}>Streamer</span>
                      <span style={{ ...thStyle, fontSize: 10 }}>💎 Diamants</span>
                      <span style={{ ...thStyle, fontSize: 10 }}>Durée live</span>
                      <span style={{ ...thStyle, fontSize: 10 }}>Jours</span>
                      <span style={{ ...thStyle, fontSize: 10, textAlign: 'center' }}>Montée niv.</span>
                      <span style={{ ...thStyle, fontSize: 10, textAlign: 'center' }}>Activité</span>
                      {isAdmin && <span style={{ ...thStyle, fontSize: 10, textAlign: 'center' }}>Progressif</span>}
                    </div>
                  )}
                  {(hasActiveFilter
                    ? team.members.filter(memberMatchesFilters)
                    : team.members
                  ).map(m => (
                    <MemberRow key={m.id} member={m} isMobile={isMobile} objectives={objectives}
                      isAdmin={isAdmin}
                      onClick={() => onSelectStreamer(m.id)} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const ROLE_STYLES: Record<string, { color: string; bg: string; label: string }> = {
  'Agent':      { color: '#1d4ed8', bg: '#dbeafe', label: 'Agent' },
  'Co-Leader':  { color: '#7c3aed', bg: '#ede9fe', label: 'Co-Leader' },
  'Recruteur':  { color: '#065f46', bg: '#d1fae5', label: 'Recruteur' },
};

const MEDALS: Record<string, string> = {
  'Agent':     '🥇',
  'Co-Leader': '🥈',
  'Recruteur': '🥉',
};

function MedalOverlay({ role, size = 40 }: { role?: string | null; size?: number }) {
  if (!role || !MEDALS[role]) return null;
  const fs = size <= 32 ? 11 : size <= 40 ? 13 : 15;
  return (
    <span style={{
      position: 'absolute', bottom: -3, right: -3,
      fontSize: fs, lineHeight: 1,
      filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.25))',
      pointerEvents: 'none',
    }}>{MEDALS[role]}</span>
  );
}

function LeadersSection({ groupe, leaders, allStreamers, isAdmin, isMobile, onLeaderAdded, onLeaderRemoved, onLeaderRoleChanged }: {
  groupe: string;
  leaders: TeamLeader[];
  allStreamers: Streamer[];
  isAdmin?: boolean;
  isMobile?: boolean;
  onLeaderAdded: (l: TeamLeader) => void;
  onLeaderRemoved: (sid: number) => void;
  onLeaderRoleChanged: (sid: number, role: string) => void;
}) {
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('Agent');
  const [saving, setSaving] = useState(false);

  const leaderIds = new Set(leaders.map(l => l.streamer_id));
  const candidates = allStreamers.filter(s =>
    !leaderIds.has(s.id) &&
    (s.display_name.toLowerCase().includes(search.toLowerCase()) ||
     s.username.toLowerCase().includes(search.toLowerCase()))
  ).slice(0, 8);

  const handleAdd = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      await addTeamLeader(groupe, selectedId, selectedRole);
      const s = allStreamers.find(x => x.id === selectedId)!;
      onLeaderAdded({ streamer_id: selectedId, display_name: s.display_name, username: s.username, avatar_url: s.avatar_url, role: selectedRole as any });
      setShowAdd(false); setSearch(''); setSelectedId(null); setSelectedRole('Agent');
    } catch {}
    setSaving(false);
  };

  const handleRoleChange = async (sid: number, role: string) => {
    try {
      await updateTeamLeaderRole(groupe, sid, role);
      onLeaderRoleChanged(sid, role);
    } catch {}
  };

  const handleRemove = async (sid: number) => {
    try {
      await removeTeamLeader(groupe, sid);
      onLeaderRemoved(sid);
    } catch {}
  };

  if (leaders.length === 0 && !isAdmin) return null;

  return (
    <div onClick={e => e.stopPropagation()} style={{ padding: isMobile ? '10px 14px' : '10px 20px 12px', background: '#fafbfc', borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: leaders.length > 0 ? 8 : 0, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.8 }}>Responsables</span>
        {leaders.map(l => (
          <div key={l.streamer_id} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 20, padding: '3px 10px 3px 6px' }}>
            {/* Avatar + médaille */}
            <div style={{ position: 'relative', width: 22, height: 22, flexShrink: 0 }}>
              <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#e2e8f0', overflow: 'hidden' }}>
                {l.avatar_url
                  ? <img src={l.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: '#64748b' }}>{l.display_name.slice(0, 2).toUpperCase()}</div>}
              </div>
              <MedalOverlay role={l.role} size={22} />
            </div>
            <span style={{ fontSize: 12, fontWeight: 500, color: '#0f172a' }}>{l.display_name}</span>
            {/* Role badge — clickable for admin */}
            {isAdmin ? (
              <select value={l.role}
                onChange={e => handleRoleChange(l.streamer_id, e.target.value)}
                onClick={e => e.stopPropagation()}
                style={{ fontSize: 10, fontWeight: 700, color: ROLE_STYLES[l.role]?.color || '#374151', background: ROLE_STYLES[l.role]?.bg || '#f1f5f9', border: 'none', borderRadius: 10, padding: '1px 4px', cursor: 'pointer', outline: 'none' }}>
                {Object.keys(ROLE_STYLES).map(r => <option key={r} value={r}>{MEDALS[r]} {r}</option>)}
              </select>
            ) : (
              <span style={{ fontSize: 10, fontWeight: 700, color: ROLE_STYLES[l.role]?.color || '#374151', background: ROLE_STYLES[l.role]?.bg || '#f1f5f9', borderRadius: 10, padding: '1px 6px' }}>{MEDALS[l.role]} {l.role}</span>
            )}
            {isAdmin && (
              <button onClick={e => { e.stopPropagation(); handleRemove(l.streamer_id); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 13, padding: 0, lineHeight: 1, marginLeft: 2 }}
                title="Retirer">×</button>
            )}
          </div>
        ))}
        {isAdmin && (
          <button onClick={e => { e.stopPropagation(); setShowAdd(v => !v); }}
            style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, border: '1px dashed #cbd5e1', background: 'transparent', color: '#64748b', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            + Responsable
          </button>
        )}
      </div>

      {/* Add picker */}
      {showAdd && isAdmin && (
        <div onClick={e => e.stopPropagation()} style={{ marginTop: 8, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input autoFocus value={search} onChange={e => { setSearch(e.target.value); setSelectedId(null); }}
              placeholder="Rechercher un streamer…"
              style={{ flex: 1, padding: '6px 10px', borderRadius: 7, border: '1px solid #e2e8f0', fontSize: 13, outline: 'none', minWidth: 160 }} />
            <select value={selectedRole} onChange={e => setSelectedRole(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 7, border: '1px solid #e2e8f0', fontSize: 13, outline: 'none', background: '#fff' }}>
              {Object.keys(ROLE_STYLES).map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          {search && candidates.length > 0 && !selectedId && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 160, overflowY: 'auto' }}>
              {candidates.map(s => (
                <div key={s.id} onClick={() => { setSelectedId(s.id); setSearch(s.display_name); }}
                  style={{ padding: '5px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#0f172a', background: '#f8fafc', display: 'flex', alignItems: 'center', gap: 8 }}
                  onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = '#eff6ff'}
                  onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = '#f8fafc'}>
                  <span style={{ fontWeight: 500 }}>{s.display_name}</span>
                  <span style={{ color: '#94a3b8', fontSize: 11 }}>@{s.username}</span>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => { setShowAdd(false); setSearch(''); setSelectedId(null); }}
              style={{ padding: '5px 12px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', fontSize: 12, cursor: 'pointer', color: '#64748b' }}>
              Annuler
            </button>
            <button onClick={handleAdd} disabled={!selectedId || saving}
              style={{ padding: '5px 14px', borderRadius: 7, border: 'none', background: selectedId ? '#2563eb' : '#e2e8f0', color: selectedId ? '#fff' : '#94a3b8', fontSize: 12, fontWeight: 600, cursor: selectedId ? 'pointer' : 'not-allowed' }}>
              {saving ? 'Ajout…' : 'Ajouter'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MemberRow({ member: m, isMobile, objectives, isAdmin, onClick }: { member: TeamMember; isMobile?: boolean; objectives?: Objectives; isAdmin?: boolean; onClick: () => void }) {
  const [hover, setHover] = useState(false);
  const initials = m.display_name.slice(0, 2).toUpperCase();
  const isLive = !!m.tiktok_live;
  const hasRemu = (m.remu_niveau || 0) + (m.remu_activite || 0) + (m.remu_progressif || 0) > 0;
  const leaderRole = m.leader_role;

  if (isMobile) {
    return (
      <div onClick={onClick}
        style={{ padding: '10px 14px', cursor: 'pointer', borderTop: '1px solid #f1f5f9', background: hover ? '#f8fafc' : 'transparent', transition: 'background 0.1s' }}
        onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
        {/* Avatar + nom */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <LiveAvatar url={m.avatar_url} initials={initials} size={32} isLive={isLive} />
            <MedalOverlay role={leaderRole} size={32} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: '#0f172a', fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.display_name}</div>
            <div style={{ color: '#94a3b8', fontSize: 11 }}>@{m.username}</div>
          </div>
          {/* Quick stats inline */}
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <MiniStat label="💎" value={fmtNum(m.diamonds)} right />
            <MiniStat label="Jours" value={m.live_days != null ? String(m.live_days) : '—'} right />
            <MiniStat label="Heures" value={fmtDuration(m.live_duration_minutes)} right />
          </div>
        </div>
        {/* Jauge objectifs */}
        {objectives && (
          <div style={{ paddingLeft: 42, marginBottom: hasRemu ? 6 : 0 }}>
            <MemberObjectiveGauge m={m} obj={objectives} />
          </div>
        )}
        {/* Remu pills row */}
        {hasRemu && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', paddingLeft: 42 }}>
            {(m.remu_niveau || 0) > 0 && (
              <RemuPill label="Montée" value={`$${m.remu_niveau!.toFixed(2)}`} color="#15803d" bg="#f0fdf4" small />
            )}
            {(m.remu_activite || 0) > 0 && (
              <RemuPill label="Activité" value={`$${m.remu_activite!.toFixed(2)}`} color="#2563eb" bg="#eff6ff" small />
            )}
            {isAdmin && (m.remu_progressif || 0) > 0 && (
              <RemuPill label="Progressif" value={`$${m.remu_progressif!.toFixed(2)}`} color="#7c3aed" bg="#f5f3ff" small />
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ display: 'grid', gridTemplateColumns: isAdmin ? '1fr 120px 100px 60px 80px 80px 80px' : '1fr 120px 100px 60px 80px 80px', gap: 8, padding: '10px 20px', alignItems: 'center', cursor: 'pointer', background: hover ? '#f8fafc' : 'transparent', transition: 'background 0.1s' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        {objectives
          ? <MemberObjectiveGaugeVertical m={m} obj={objectives} size={34} />
          : <div style={{ width: 8, flexShrink: 0 }} />}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <LiveAvatar url={m.avatar_url} initials={initials} size={34} isLive={isLive} />
          <MedalOverlay role={leaderRole} size={34} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: '#0f172a', fontWeight: 500, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.display_name}</div>
          <div style={{ color: '#94a3b8', fontSize: 11 }}>@{m.username}</div>
        </div>
      </div>
      <div style={{ color: '#0f172a', fontSize: 13, fontWeight: m.diamonds ? 600 : 400 }}>{fmtNum(m.diamonds)}</div>
      <div style={{ color: '#64748b', fontSize: 13 }}>{fmtDuration(m.live_duration_minutes)}</div>
      <div style={{ color: '#64748b', fontSize: 13 }}>{m.live_days != null ? m.live_days : '—'}</div>
      <div style={{ color: (m.remu_niveau || 0) > 0 ? '#15803d' : '#94a3b8', fontSize: 12, fontWeight: 600, textAlign: 'center' }}>
        {(m.remu_niveau || 0) > 0 ? `$${m.remu_niveau!.toFixed(2)}` : '—'}
      </div>
      <div style={{ color: (m.remu_activite || 0) > 0 ? '#2563eb' : '#94a3b8', fontSize: 12, fontWeight: 600, textAlign: 'center' }}>
        {(m.remu_activite || 0) > 0 ? `$${m.remu_activite!.toFixed(2)}` : '—'}
      </div>
      {isAdmin && (
        <div style={{ color: (m.remu_progressif || 0) > 0 ? '#7c3aed' : '#94a3b8', fontSize: 12, fontWeight: 600, textAlign: 'center' }}>
          {(m.remu_progressif || 0) > 0 ? `$${m.remu_progressif!.toFixed(2)}` : '—'}
        </div>
      )}
    </div>
  );
}

function memberObjColor(pct: number): string {
  if (pct >= 1)   return '#16a34a';
  if (pct >= 0.5) return '#ea580c';
  return '#dc2626';
}

function MemberObjectiveGauge({ m, obj }: { m: TeamMember; obj: Objectives }) {
  if (m.diamonds == null && m.live_days == null) return null;
  const metrics = [
    { value: m.live_days ?? 0,                                          target: obj.days },
    { value: Math.floor((m.live_duration_minutes ?? 0) / 60),           target: obj.hours },
    { value: m.diamonds ?? 0,                                           target: obj.diamonds },
  ];
  return (
    <div style={{ width: '100%', display: 'flex', gap: 2 }}>
      {metrics.map((mt, i) => {
        const pct = Math.min(1, mt.target > 0 ? mt.value / mt.target : 0);
        const radius = i === 0 ? '4px 0 0 4px' : i === 2 ? '0 4px 4px 0' : '0';
        return (
          <div key={i} style={{ flex: 1, height: 6, background: memberObjColor(pct), borderRadius: radius, transition: 'background 0.4s' }} />
        );
      })}
    </div>
  );
}

/** Thermomètre vertical pour MemberRow desktop */
function MemberObjectiveGaugeVertical({ m, obj, size }: { m: TeamMember; obj: Objectives; size: number }) {
  if (m.diamonds == null && m.live_days == null) return <div style={{ width: 8, flexShrink: 0 }} />;
  const metrics = [
    { value: m.live_days ?? 0,                                  target: obj.days },
    { value: Math.floor((m.live_duration_minutes ?? 0) / 60),   target: obj.hours },
    { value: m.diamonds ?? 0,                                   target: obj.diamonds },
  ];
  const cellH = Math.floor((size - 4) / 3);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, width: 8, height: size, flexShrink: 0 }}>
      {[...metrics].reverse().map((mt, i) => {
        const pct = Math.min(1, mt.target > 0 ? mt.value / mt.target : 0);
        const radius = i === 0 ? '4px 4px 0 0' : i === 2 ? '0 0 4px 4px' : '0';
        return <div key={i} style={{ height: cellH, background: memberObjColor(pct), borderRadius: radius, transition: 'background 0.4s' }} />;
      })}
    </div>
  );
}

function MiniStat({ label, value, right }: { label: string; value: string; right?: boolean }) {
  return (
    <div style={{ textAlign: right ? 'right' : 'left' }}>
      <div style={{ color: '#94a3b8', fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ color: '#0f172a', fontSize: 12, fontWeight: 600 }}>{value}</div>
    </div>
  );
}

function RemuPill({ label, value, color, bg, small }: { label: string; value: string; color: string; bg: string; small?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: bg, borderRadius: 6, padding: small ? '2px 7px' : '4px 10px' }}>
      <span style={{ color: '#94a3b8', fontSize: small ? 9 : 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</span>
      <span style={{ color, fontSize: small ? 11 : 13, fontWeight: 700 }}>{value}</span>
    </div>
  );
}


// ── Constantes tableaux rému équipe ──────────────────────
const TV_NIVEAU_LABELS   = ['Niv.1','Niv.2','Niv.3','Niv.4','Niv.5','Niv.6','Niv.7','Niv.8','Niv.9','Niv.10'];
const TV_NIVEAU_DIAMONDS = ['≥ 0','≥ 100K','≥ 200K','≥ 300K','≥ 500K','≥ 700K','≥ 1M','≥ 1,6M','≥ 2,5M','≥ 5M'];
const TV_NULL_MONTEE     = [false,false,false,false,false,false,false,false,false,true];
const TV_NULL_NON_MAINT  = [true,false,false,false,false,false,false,false,false,false];
const TV_ACTIVITE_LABELS = ['Niv.1 (≥8j, ≥20h)','Niv.2 (≥11j, ≥30h)','Niv.3 (≥15j, ≥40h)','Niv.4 (≥18j, ≥60h)','Niv.5 (≥22j, ≥80h)'];

const TV_DEFAULT_NR: NiveauRates = {
  montee:       [0,0,0,0,0,0,0,0,0,null],
  maintien:     [0,0,0,0,0,0,0,0,0,0],
  non_maintenu: [null,0,0,0,0,0,0,0,0,0],
};
const TV_DEFAULT_AR: (number|null)[] = [0,0,0,0,0];

function tvRateToStr(v: number|null): string { return v === null ? '' : String(v); }
function tvStrToRate(s: string, isNull: boolean): number|null {
  if (isNull && s === '') return null;
  const n = parseFloat(s); return isNaN(n) ? 0 : n;
}

function RemuSection({ groupe, remu, isAdmin, activePeriod, onSaved }: {
  groupe: string;
  remu: TeamRemu | undefined;
  isAdmin?: boolean;
  activePeriod?: string;
  onSaved: (r: TeamRemu) => void;
}) {
  const isEnabled = remu ? Boolean(remu.enabled) : false;
  const [editing, setEditing] = useState(false);
  const editRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (editing && editRef.current) {
      setTimeout(() => editRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
  }, [editing]);
  const [editEnabled, setEditEnabled] = useState(isEnabled);
  const [editMontee, setEditMontee]     = useState<string[]>(() => (remu?.niveau_rates?.montee    || TV_DEFAULT_NR.montee).map(tvRateToStr));
  const [editMaintien, setEditMaintien] = useState<string[]>(() => (remu?.niveau_rates?.maintien  || TV_DEFAULT_NR.maintien).map(tvRateToStr));
  const [editNonMaint, setEditNonMaint] = useState<string[]>(() => (remu?.niveau_rates?.non_maintenu || TV_DEFAULT_NR.non_maintenu).map(tvRateToStr));
  const [editActivite, setEditActivite] = useState<string[]>(() => (remu?.activite_rates || TV_DEFAULT_AR).map(tvRateToStr));
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (remu) {
      setEditEnabled(Boolean(remu.enabled));
      setEditMontee((remu.niveau_rates?.montee    || TV_DEFAULT_NR.montee).map(tvRateToStr));
      setEditMaintien((remu.niveau_rates?.maintien || TV_DEFAULT_NR.maintien).map(tvRateToStr));
      setEditNonMaint((remu.niveau_rates?.non_maintenu || TV_DEFAULT_NR.non_maintenu).map(tvRateToStr));
      setEditActivite((remu.activite_rates || TV_DEFAULT_AR).map(tvRateToStr));
    }
  }, [remu]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const niveau_rates: NiveauRates = {
        montee:       editMontee.map((v,i) => tvStrToRate(v, TV_NULL_MONTEE[i])),
        maintien:     editMaintien.map(v  => tvStrToRate(v, false)),
        non_maintenu: editNonMaint.map((v,i) => tvStrToRate(v, TV_NULL_NON_MAINT[i])),
      };
      const activite_rates = editActivite.map(v => tvStrToRate(v, false));
      await updateTeamRemu(groupe, { enabled: editEnabled, niveau_rates, activite_rates });
      const r = await getTeamRemu(groupe, activePeriod);
      onSaved(r.data);
      setEditing(false);
    } catch {}
    setSaving(false);
  };

  const inp: React.CSSProperties = { width: 52, padding: '3px 4px', borderRadius: 5, border: '1px solid #cbd5e1', fontSize: 11, textAlign: 'center', boxSizing: 'border-box' };
  const naCell: React.CSSProperties = { color: '#94a3b8', fontSize: 12, textAlign: 'center' };

  const formContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Toggle */}
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
        <div onClick={() => setEditEnabled(e => !e)} style={{ width: 36, height: 20, borderRadius: 10, background: editEnabled ? '#16a34a' : '#d1d5db', position: 'relative', cursor: 'pointer', transition: 'background 0.2s' }}>
          <div style={{ position: 'absolute', top: 2, left: editEnabled ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
        </div>
        <span style={{ fontSize: 13, color: '#374151', fontWeight: editEnabled ? 600 : 400 }}>
          {editEnabled ? 'Rémunération activée' : 'Rémunération désactivée'}
        </span>
      </label>

      {editEnabled && (<>
        {/* Table Montée de niveau */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 4 }}>📈 Montée de niveau</div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>Conditions : ≥ 7 jours live ET ≥ 15h mensuel</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: 11, width: '100%' }}>
              <thead><tr style={{ background: '#f8fafc' }}>
                <th style={{ padding: '5px 8px', textAlign: 'left', color: '#64748b', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>Niveau</th>
                <th style={{ padding: '5px 8px', textAlign: 'center', color: '#64748b', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>Diamants</th>
                <th style={{ padding: '5px 8px', textAlign: 'center', color: '#16a34a', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>Montée (%)</th>
                <th style={{ padding: '5px 8px', textAlign: 'center', color: '#ca8a04', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>Maintien (%)</th>
                <th style={{ padding: '5px 8px', textAlign: 'center', color: '#dc2626', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>Non maintenu (%)</th>
              </tr></thead>
              <tbody>{TV_NIVEAU_LABELS.map((label, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '4px 8px', fontWeight: 600, color: '#374151' }}>{label}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'center', color: '#64748b' }}>{TV_NIVEAU_DIAMONDS[i]}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                    {TV_NULL_MONTEE[i] ? <span style={naCell}>—</span> : <input type="number" min="0" max="100" step="0.1" value={editMontee[i]} onChange={e => setEditMontee(p => { const a=[...p]; a[i]=e.target.value; return a; })} style={inp} />}
                  </td>
                  <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                    <input type="number" min="0" max="100" step="0.1" value={editMaintien[i]} onChange={e => setEditMaintien(p => { const a=[...p]; a[i]=e.target.value; return a; })} style={inp} />
                  </td>
                  <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                    {TV_NULL_NON_MAINT[i] ? <span style={naCell}>—</span> : <input type="number" min="0" max="100" step="0.1" value={editNonMaint[i]} onChange={e => setEditNonMaint(p => { const a=[...p]; a[i]=e.target.value; return a; })} style={inp} />}
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>

        {/* Table Activité */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 4 }}>⚡ Incitation à l'activité</div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>Condition : ≥ 100 diamants mensuels</div>
          <table style={{ borderCollapse: 'collapse', fontSize: 11 }}>
            <thead><tr style={{ background: '#f8fafc' }}>
              <th style={{ padding: '5px 10px', textAlign: 'left', color: '#64748b', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>Palier</th>
              <th style={{ padding: '5px 10px', textAlign: 'center', color: '#16a34a', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>Taux bonus (%)</th>
            </tr></thead>
            <tbody>{TV_ACTIVITE_LABELS.map((label, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '4px 10px', color: '#374151' }}>{label}</td>
                <td style={{ padding: '4px 10px', textAlign: 'center' }}>
                  <input type="number" min="0" max="100" step="0.1" value={editActivite[i]} onChange={e => setEditActivite(p => { const a=[...p]; a[i]=e.target.value; return a; })} style={inp} />
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </>)}
    </div>
  );

  return (
    <>
      <div ref={editRef} style={{ margin: '12px 0 0', padding: '12px 16px', background: isEnabled ? '#f0fdf4' : '#f8fafc', borderRadius: 10, border: `1px solid ${isEnabled ? '#bbf7d0' : '#e2e8f0'}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 15 }}>🏅</span>
          <span style={{ fontWeight: 600, fontSize: 13, color: isEnabled ? '#15803d' : '#64748b', flex: 1 }}>
            Rémunération équipe
          </span>
          {isEnabled && remu?.total_team_remu != null && remu.total_team_remu > 0 && (
            <span style={{ fontWeight: 700, color: '#16a34a', fontSize: 13 }}>${remu.total_team_remu!.toFixed(2)}</span>
          )}
          {!isEnabled && <span style={{ fontSize: 11, color: '#94a3b8' }}>Désactivée</span>}
          {isAdmin && (
            <button onClick={() => setEditing(true)} style={{ background: 'none', border: '1px solid #cbd5e1', borderRadius: 6, padding: '3px 10px', fontSize: 12, cursor: 'pointer', color: '#64748b' }}>
              {isEnabled ? 'Modifier' : 'Configurer'}
            </button>
          )}
        </div>
      </div>

      {/* ── Modale de configuration (overlay plein écran, scroll propre) ── */}
      {editing && (
        <div
          onClick={() => setEditing(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16, zIndex: 9999,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 14, width: '100%', maxWidth: 640,
              maxHeight: '90vh', display: 'flex', flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            }}
          >
            {/* Header sticky */}
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
              <span style={{ fontSize: 18 }}>🏅</span>
              <div style={{ flex: 1, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                Rémunération — {groupe}
              </div>
              <button
                onClick={() => setEditing(false)}
                aria-label="Fermer"
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#94a3b8', padding: 4, lineHeight: 1 }}
              >×</button>
            </div>

            {/* Body scrollable */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 18, minHeight: 0 }}>
              {formContent}
            </div>

            {/* Footer sticky */}
            <div style={{ padding: '12px 18px', borderTop: '1px solid #e2e8f0', display: 'flex', gap: 10, justifyContent: 'flex-end', flexShrink: 0, background: '#f8fafc', borderRadius: '0 0 14px 14px' }}>
              <button onClick={() => setEditing(false)}
                style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer', color: '#64748b', fontWeight: 600 }}>
                Annuler
              </button>
              <button onClick={handleSave} disabled={saving}
                style={{ background: '#0ea5e9', border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 13, cursor: 'pointer', color: '#fff', fontWeight: 700, opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// labelStyle moved to StreamerList (summary banner lifted there)

const thStyle: React.CSSProperties = {
  color: '#94a3b8', fontSize: 11, fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: 0.8,
};
